/* eslint-disable react/prop-types */
import PropTypes from 'prop-types';
import { Prompt } from 'react-router-dom';
import WistiaUtils from 'edrolo/utils/WistiaUtils';
import { QUESTION_GROKKED } from 'edrolo/domains/exams';
import QuestionSolution from '../../../QuestionSolution';
import SectionHeading from '../../../SectionHeading';
import GrokSelfReflection from '../../../GrokSelfReflection';
import { SelfMarking } from '../SelfMarking';
import EquationSolution from 'edrolo/components/exams/solutions/EquationSolution';
import {
  parseEquationsFromResponse,
  isEmptyResponse,
} from './utils/equationParser';
import styles from './styles.scss';

const ComplexSolutionAnswer = ({
  question,
  response,
  onSubmitGrok,
  onSubmitMark,
  grokkingItemIsSaving = false,
  showIncompleteChecklistWarning = false,
}) => {
  let submittedEquations = [];
  let parseError = null;
  let submittedText = null;

  try {
    submittedEquations = parseEquationsFromResponse(response);
    submittedText = response?.get
      ? response.get('text_submitted_text')
      : response?.text_submitted_text;
  } catch (error) {
    console.error('Error parsing submitted Complex Solution response:', error);
    parseError = error.message;
    submittedEquations = [''];
    try {
      const raw = response?.get
        ? response.get('text_submitted_text')
        : response?.text_submitted_text;
      submittedText =
        typeof raw === 'string'
          ? raw
          : raw != null
          ? JSON.stringify(raw)
          : null;
    } catch {
      submittedText = null;
    }
  }

  const isEmpty = isEmptyResponse(submittedEquations);
  const questionId = question?.get ? question.get('id') : question?.id;
  const marks =
    Number(question?.get ? question.get('marks') : question?.marks) || 1;
  const studentMarks =
    Number(
      response?.get
        ? response.get('text_marks_from_student')
        : response?.text_marks_from_student
    ) || 0;
  const grokValue = response?.get ? response.get('grokked') : response?.grokked;
  const isSelfReflectionComplete = !(
    grokValue == null || grokValue === QUESTION_GROKKED.NO_RESPONSE
  );

  const explanation = response?.getIn
    ? response.getIn(['solution', 'explanation'])
    : response?.solution?.explanation;
  const videoHash = response?.getIn
    ? response.getIn(['solution', 'video', 'wistia_hashed_id'])
    : response?.solution?.video?.wistia_hashed_id;
  const videoData = response?.getIn
    ? response.getIn(['solution', 'video'])
    : response?.solution?.video;
  const videoThumbnailUrl = WistiaUtils.getThumbnail(videoData);

  const message =
    !isSelfReflectionComplete && showIncompleteChecklistWarning
      ? 'Your response to the self-reflection is incomplete.\nAre you sure you want to leave this question?'
      : null;

  return (
    <div
      className={styles.complexSolutionAnswer}
      data-testid="complex-solution-answer"
    >
      {message && <Prompt message={message} />}

      <QuestionSolution
        headingText="Solution"
        questionId={questionId}
        explanation={explanation}
        videoHash={videoHash}
        videoThumbnailUrl={videoThumbnailUrl}
      />

      <div className={styles.submittedResponse}>
        <SectionHeading>Your Response</SectionHeading>

        {parseError ? (
          <div className={styles.parseErrorDisplay}>
            <div className={styles.errorMessage}>
              <h6>⚠️ Response Display Error</h6>
              <p>There was an issue displaying your submitted response.</p>
              <small>Error: {parseError}</small>
            </div>
            {submittedText && (
              <details className={styles.rawDataDetails}>
                <summary>Raw response data</summary>
                <pre>{submittedText}</pre>
              </details>
            )}
          </div>
        ) : (
          <div className={styles.equationDisplay}>
            {!isEmpty ? (
              <EquationSolution equations={submittedEquations} />
            ) : (
              <div className={styles.emptyResponse}>
                <p>No equations were submitted.</p>
              </div>
            )}
          </div>
        )}
      </div>

      <SelfMarking
        onChange={onSubmitMark}
        mark={studentMarks}
        totalMarks={marks}
      />

      <GrokSelfReflection
        value={grokValue}
        onChange={onSubmitGrok}
        isSaving={grokkingItemIsSaving}
        className={styles.selfReflection}
      />
    </div>
  );
};

ComplexSolutionAnswer.propTypes = {
  question: PropTypes.object.isRequired,
  response: PropTypes.object.isRequired,
  onSubmitGrok: PropTypes.func.isRequired,
  onSubmitMark: PropTypes.func.isRequired,
  grokkingItemIsSaving: PropTypes.bool,
  showIncompleteChecklistWarning: PropTypes.bool,
};

export { ComplexSolutionAnswer };
